import ls from 'localstorage-slim';
import type { DemoConfig } from 'config/demo.config';
import type {
    AuthenticateResponse,
    BalanceResponse,
    EndRoundResponse,
    PlayResponse,
    Round,
} from 'stake-engine';
import type { RoundState } from '../../../utils/api.types';

/**
 * Book win amounts are minor units (2780 -> 27.80), balances are raw RGS amounts
 * (27.80 -> 27_800_000), so a payout is scaled by the difference between the two.
 */
const MINOR_UNITS_TO_RAW = 10_000;

export class DemoClient {
    #demo: DemoConfig;
    #state: AuthenticateResponse;
    #roundState: RoundState[] | null = null;

    constructor(demo: DemoConfig) {
        this.#demo = demo;
        this.#state = demo.initState;

        // TODO: move to store
        const saved = ls.get<AuthenticateResponse>('demoState');
        if (saved) {
            this.#state = saved;

            if (saved.round?.active) {
                // keep serving the same active round (same betID) after a refresh so
                // the real backend's "round stays open until EndRound" behaviour is
                // simulated and an in-flight free spins round can be resumed
                this.#roundState = saved.round.state as RoundState[];
            } else {
                // the saved round was settled, and a state with no round leaves the
                // reels on a random board. Re-open on the configured opening round
                // so the demo always starts from the same, known result.
                this.#state = { ...saved, round: demo.initState.round };
            }
        }
    }

    async Authenticate(): Promise<AuthenticateResponse> {
        return this.state;
    }

    private generateRoundState(): RoundState[] {
        const { rounds, activeRound } = this.#demo;
        const round = rounds[activeRound];

        if (!round) {
            throw new Error(
                `Demo round "${activeRound}" is missing — known rounds: ${Object.keys(rounds).join(', ')}`,
            );
        }

        return round;
    }

    async Play(bet: { amount: number; mode: string }): Promise<PlayResponse> {
        this.state.balance.amount -= bet.amount;

        if (!this.#roundState) {
            this.#roundState = this.generateRoundState();
        }

        const round: Round = {
            betID: Date.now(),
            active: true,
            mode: bet.mode,
            state: this.#roundState,
        };

        // persist through the setter so a mid-round refresh restores this round
        this.state = { ...this.#state, round };

        return {
            balance: this.#state.balance,
            round,
        };
    }

    async EndRound(): Promise<EndRoundResponse> {
        // the RGS credits the round's payout on EndRound, not on Play — mirror
        // that here, otherwise a demo balance only ever goes down and a win
        // never lands on screen
        const balance = {
            ...this.#state.balance,
            amount: this.#state.balance.amount + this.payout(),
        };

        this.#roundState = null;
        this.state = { ...this.#state, balance, round: null };

        return { balance };
    }

    /**
     * What the round being settled paid, in raw RGS units — the closing
     * `finalWin` of the book that was served for it. Read off the round rather
     * than `#roundState`, which is only filled in by `Play`: the round the demo
     * opens on (and the one restored from a refresh) is served straight out of
     * the state and has to pay out too.
     */
    private payout(): number {
        const states = (this.#state.round?.state ?? []) as RoundState[];
        const finalWin = states
            .filter((state): state is Extract<RoundState, { type: 'finalWin' }> => {
                return state.type === 'finalWin';
            })
            .at(-1);

        return Number(finalWin?.amount ?? 0) * MINOR_UNITS_TO_RAW;
    }

    async Balance(): Promise<BalanceResponse> {
        return { balance: this.#state.balance };
    }

    async Event(event: string) {
        console.log(`🧟 Demo event`, event);
    }

    get state(): AuthenticateResponse {
        return this.#state;
    }

    set state(state: AuthenticateResponse) {
        this.#state = state;

        ls.set('demoState', this.#state);
    }
}
